import type { CustomerLookupItem } from './api-contracts.js';
import type { ContractDocument, ContractType } from './contract-document.js';
import { CURRENT_CONTRACT_SCHEMA_VERSION } from './contract-document.js';
import { personRef, roleRef } from './external-refs.js';
import type { CostRatePeriod, PersonRef, RoleRef } from './external-refs.js';
import type { PlanNode, ProjectDocument } from './project-document.js';
import { CURRENT_DOCUMENT_SCHEMA_VERSION } from './project-document.js';

/**
 * "Project Ship P26.0007" — the canonical fixture, faithful to the design
 * prototypes (Planning v2, Contract A) with one deliberate correction: the
 * prototype's "Backend implementation" carried both its own 180 h and a child
 * task, which BR-1 forbids (its hours were invisible to the rollup anyway), so
 * here it is a pure parent node.
 *
 * The numbers reproduce the worked examples in docs/specs/calculations.md:
 * Contract A (fixed price) costs 489,450.55 (×2.45 — Contingency carries no
 * cost, BR-14); its Own Work hours are
 * the PM's typed 400 h (D11), so its average rate is 1,800/h (CALC-23) and the
 * plan consumes 350 h / 630,000 DKK of it; the T&M Contract B prices by role
 * and the plan consumes 70 h / 70,000 DKK of it.
 *
 * Role ids are hourly-rate ServiceIDs (D1); SHIP_STANDARD_RATE_CARD is the
 * tenant's default hourly-rate list, which the fixture TimeLog also serves.
 *
 * Used by db:seed, the API e2e suite and the web app's sample-creation flow.
 */

export const PROJECT_SHIP_NAME = 'Project Ship';
export const PROJECT_SHIP_CODE = 'P26.0007';

/** Stable ids so seeds, tests and actuals fixtures agree. */
export const SHIP_CONTRACT_IDS = {
  a: '7a1e4d2c-3b5f-4a6e-8c9d-0f1a2b3c4d5e',
  b: '2b3c4d5e-6f7a-4b8c-9d0e-1f2a3b4c5d6e',
  c: '3c4d5e6f-7a8b-4c9d-8e0f-2a3b4c5d6e7f',
} as const;

export const SHIP_NODE_IDS = {
  discovery: '00000000-0000-4000-8000-00000000d001',
  build: '00000000-0000-4000-8000-00000000d002',
  testDeploy: '00000000-0000-4000-8000-00000000d003',
  workshops: '00000000-0000-4000-8000-00000000a101',
  solutionDesign: '00000000-0000-4000-8000-00000000a102',
  backendImplementation: '00000000-0000-4000-8000-00000000a103',
  apiIntegration: '00000000-0000-4000-8000-00000000a104',
  frontendImplementation: '00000000-0000-4000-8000-00000000a105',
  qaTesting: '00000000-0000-4000-8000-00000000a106',
  deployment: '00000000-0000-4000-8000-00000000a107',
} as const;

// TimeLog-side reference data (fixtures shaped exactly as production snapshots).
export const SHIP_ROLES: Record<string, RoleRef> = {
  pm: roleRef('pm', 'Project management'),
  arch: roleRef('arch', 'Solution architect'),
  dev: roleRef('dev', 'Developer'),
  qa: roleRef('qa', 'QA / test'),
  devops: roleRef('devops', 'DevOps'),
};

/**
 * The people as they appear ON the plan: snapshots captured before employee
 * cost rates were exposed, so they carry none and cost falls through to the
 * contract rule (CALC-21) — which keeps the worked examples integer-clean.
 */
export const SHIP_PEOPLE: Record<string, PersonRef> = {
  anders: personRef('u1', 'Anders Holm'),
  mette: personRef('u2', 'Mette Sørensen'),
  jonas: personRef('u3', 'Jonas Berg'),
  sofia: personRef('u4', 'Sofia Lind'),
  lars: personRef('u5', 'Lars Poulsen'),
  nina: personRef('u6', 'Nina Dahl'),
};

/**
 * The employee cards behind SHIP_PEOPLE, as the fixture TimeLog exposes them:
 * the default role (an hourly-rate ServiceID, D5) and the time-phased cost rate
 * (D14) — one open-ended period each, so the numbers hold on any day. Live
 * TimeLog exposes neither yet; the fixture is where the rules are exercised.
 */
export const SHIP_EMPLOYEES: Record<
  string,
  { defaultRoleServiceId: string; costRates: CostRatePeriod[] }
> = {
  u1: {
    defaultRoleServiceId: 'pm',
    costRates: [{ validFrom: null, validTo: null, costRate: 700 }],
  },
  u2: {
    defaultRoleServiceId: 'arch',
    costRates: [{ validFrom: null, validTo: null, costRate: 750 }],
  },
  u3: {
    defaultRoleServiceId: 'dev',
    costRates: [{ validFrom: null, validTo: null, costRate: 600 }],
  },
  u4: {
    defaultRoleServiceId: 'dev',
    costRates: [{ validFrom: null, validTo: null, costRate: 600 }],
  },
  u5: {
    defaultRoleServiceId: 'qa',
    costRates: [{ validFrom: null, validTo: null, costRate: 500 }],
  },
  u6: {
    defaultRoleServiceId: 'devops',
    costRates: [{ validFrom: null, validTo: null, costRate: 650 }],
  },
};

/**
 * Deterministic customer list for the fixture lookup provider. `cust-maersk`
 * matches the customer snapshot on the Project Ship document so the sample
 * flow and the picker agree.
 */
export const SHIP_CUSTOMERS: CustomerLookupItem[] = [
  { tlpId: 'cust-maersk', name: 'Maersk A/S', customerNo: '1001', city: 'København' },
  { tlpId: 'cust-novo', name: 'Novo Nordisk A/S', customerNo: '1002', city: 'Bagsværd' },
  { tlpId: 'cust-lego', name: 'LEGO System A/S', customerNo: '1003', city: 'Billund' },
  { tlpId: 'cust-vestas', name: 'Vestas Wind Systems A/S', customerNo: '1004', city: 'Aarhus' },
  {
    tlpId: 'cust-carlsberg',
    name: 'Carlsberg Breweries A/S',
    customerNo: '1005',
    city: 'København',
  },
];

/**
 * The tenant's default hourly-rate list (D1, D15): the roles a task offers when
 * it has no contract or a fixed-price one, and the seed for a new T&M card.
 */
export const SHIP_STANDARD_RATE_CARD = [
  { id: '00000000-0000-4000-8000-00000000c001', roleRef: SHIP_ROLES.pm!, rate: 1400 },
  { id: '00000000-0000-4000-8000-00000000c002', roleRef: SHIP_ROLES.arch!, rate: 1500 },
  { id: '00000000-0000-4000-8000-00000000c003', roleRef: SHIP_ROLES.dev!, rate: 1200 },
  { id: '00000000-0000-4000-8000-00000000c004', roleRef: SHIP_ROLES.qa!, rate: 1000 },
  { id: '00000000-0000-4000-8000-00000000c005', roleRef: SHIP_ROLES.devops!, rate: 1250 },
];

export type ProjectShipContract = {
  id: string;
  name: string;
  type: ContractType;
  document: ContractDocument;
};

export const createProjectShipContracts = (): ProjectShipContract[] => [
  {
    id: SHIP_CONTRACT_IDS.a,
    name: 'Phase 1 — Build',
    type: 'fixed',
    document: {
      schemaVersion: CURRENT_CONTRACT_SCHEMA_VERSION,
      currency: 'DKK',
      revenue: 1_200_000,
      targetFactor: 2.1,
      categories: [
        // 400 h typed by the PM (D11): cost 257,142.86 ÷ 400 = 642.86/h implied,
        // 720,000 ÷ 400 = 1,800/h average (CALC-23), and 1,800 / 642.86 = ×2.8.
        { id: 'own', amount: 720_000, targetFactor: 2.8, hidden: false, ownHoursOverride: 400 },
        { id: 'cont', amount: 180_000, targetFactor: 2.1, hidden: false },
        {
          id: 'exp',
          amount: 120_000,
          targetFactor: 1.2,
          hidden: false,
          split: {
            kind: 'rows',
            rows: [
              { id: '00000000-0000-4000-8000-00000000e001', name: 'Mileage', sharePct: 40 },
              { id: '00000000-0000-4000-8000-00000000e002', name: 'Allowance', sharePct: 30 },
              { id: '00000000-0000-4000-8000-00000000e003', name: 'Expense', sharePct: 30 },
            ],
          },
        },
        {
          id: 'sub',
          amount: 60_000,
          targetFactor: 1.5,
          hidden: false,
          split: {
            kind: 'suppliers',
            suppliers: [
              {
                id: '00000000-0000-4000-8000-00000000f001',
                name: 'Supplier A',
                sharePct: 60,
                types: [
                  { id: '00000000-0000-4000-8000-00000000f011', name: 'Development', sharePct: 70 },
                  { id: '00000000-0000-4000-8000-00000000f012', name: 'QA Testing', sharePct: 30 },
                ],
              },
              {
                id: '00000000-0000-4000-8000-00000000f002',
                name: 'Supplier B',
                sharePct: 40,
                types: [],
              },
            ],
            unassigned: [],
          },
        },
        {
          id: 'mat',
          amount: 120_000,
          targetFactor: 1.3,
          hidden: false,
          split: {
            kind: 'rows',
            rows: [
              { id: '00000000-0000-4000-8000-00000000e011', name: 'Type A', sharePct: 50 },
              { id: '00000000-0000-4000-8000-00000000e012', name: 'Type B', sharePct: 50 },
            ],
          },
        },
      ],
      // Fixed price: no rate card (D10) — hours price at the average rate.
      rateCard: [],
    },
  },
  {
    id: SHIP_CONTRACT_IDS.b,
    name: 'Support retainer',
    type: 'tm',
    document: {
      schemaVersion: CURRENT_CONTRACT_SCHEMA_VERSION,
      currency: 'DKK',
      revenue: 650_000,
      targetFactor: 2.1,
      categories: [
        { id: 'own', amount: 520_000, targetFactor: 3.1, hidden: false },
        { id: 'cont', amount: 0, targetFactor: 2.1, hidden: false },
        { id: 'exp', amount: 130_000, targetFactor: 1.2, hidden: false },
        { id: 'sub', amount: 0, targetFactor: 1.5, hidden: true },
        { id: 'mat', amount: 0, targetFactor: 1.3, hidden: true },
      ],
      rateCard: [
        { id: '00000000-0000-4000-8000-00000000c011', roleRef: SHIP_ROLES.pm!, rate: 1400 },
        { id: '00000000-0000-4000-8000-00000000c012', roleRef: SHIP_ROLES.dev!, rate: 1200 },
        { id: '00000000-0000-4000-8000-00000000c013', roleRef: SHIP_ROLES.qa!, rate: 1000 },
      ],
      tmDiscountPct: 10,
    },
  },
  {
    id: SHIP_CONTRACT_IDS.c,
    name: 'Phase 2 — Rollout',
    type: 'fixed',
    document: {
      schemaVersion: CURRENT_CONTRACT_SCHEMA_VERSION,
      currency: 'DKK',
      revenue: 480_000,
      targetFactor: 2.1,
      categories: [
        { id: 'own', amount: 288_000, targetFactor: 2.5, hidden: false },
        { id: 'cont', amount: 72_000, targetFactor: 2.1, hidden: false },
        { id: 'exp', amount: 48_000, targetFactor: 1.2, hidden: false },
        { id: 'sub', amount: 24_000, targetFactor: 1.5, hidden: false },
        { id: 'mat', amount: 48_000, targetFactor: 1.3, hidden: false },
      ],
      // Fixed price: no rate card (D10).
      rateCard: [],
    },
  },
];

/**
 * The plan, wired to the given contract ids (pass SHIP_CONTRACT_IDS when seeding
 * with fixed ids, or the server-assigned ids when creating through the API).
 */
export const createProjectShipPlan = (
  contractIds: { a: string; b: string; c: string } = SHIP_CONTRACT_IDS,
): ProjectDocument => ({
  schemaVersion: CURRENT_DOCUMENT_SCHEMA_VERSION,
  name: PROJECT_SHIP_NAME,
  code: PROJECT_SHIP_CODE,
  description: 'Canonical sample project, faithful to the design prototypes.',
  customer: {
    tlpId: 'cust-maersk',
    snapshot: { name: 'Maersk A/S' },
    capturedAt: '2026-08-04T00:00:00Z',
  },
  currency: 'DKK',
  // The prefill for tasks created from now on (D8) — every existing leaf names
  // its contract explicitly; nothing resolves through this at read time.
  defaultContractId: contractIds.a,
  blendedCostRate: 650,
  codeCounters: { deliverable: 3, task: 107 },
  plan: [
    {
      id: SHIP_NODE_IDS.discovery,
      code: 'D-1',
      kind: 'deliverable',
      name: 'Discovery & design',
      status: 'active',
      startDate: '2026-06-02',
      endDate: '2026-06-27',
      targetDate: '2026-07-03',
      allocations: [],
      children: [
        {
          id: SHIP_NODE_IDS.workshops,
          code: 'T-101',
          kind: 'task',
          name: 'Stakeholder workshops',
          status: 'active',
          startDate: '2026-06-02',
          endDate: '2026-06-13',
          targetDate: '2026-06-28',
          contractId: contractIds.a,
          roleRef: SHIP_ROLES.pm!,
          assignee: SHIP_PEOPLE.anders!,
          estimatedHours: 40,
          allocations: [],
          children: [],
        },
        {
          id: SHIP_NODE_IDS.solutionDesign,
          code: 'T-102',
          kind: 'task',
          name: 'Solution design',
          status: 'active',
          startDate: '2026-06-09',
          endDate: '2026-06-27',
          targetDate: '2026-07-03',
          contractId: contractIds.a,
          /*
            Budget AND allocations, fully staffed (BR-28): 80 h of work, 80 h
            committed. Held equal so the worked examples in
            docs/specs/calculations.md do not move.
          */
          estimatedHours: 80,
          allocations: [
            {
              id: '00000000-0000-4000-8000-00000000b001',
              roleRef: SHIP_ROLES.pm!,
              personRef: SHIP_PEOPLE.anders!,
              hours: 20,
            },
            {
              id: '00000000-0000-4000-8000-00000000b002',
              roleRef: SHIP_ROLES.arch!,
              personRef: SHIP_PEOPLE.mette!,
              hours: 40,
            },
            {
              id: '00000000-0000-4000-8000-00000000b003',
              roleRef: SHIP_ROLES.dev!,
              personRef: SHIP_PEOPLE.jonas!,
              hours: 20,
            },
          ],
          children: [],
        },
      ],
    },
    {
      id: SHIP_NODE_IDS.build,
      code: 'D-2',
      kind: 'deliverable',
      name: 'Build',
      status: 'planned',
      startDate: '2026-06-30',
      endDate: '2026-08-22',
      targetDate: '2026-08-22',
      allocations: [],
      children: [
        {
          id: SHIP_NODE_IDS.backendImplementation,
          code: 'T-103',
          kind: 'task',
          name: 'Backend implementation',
          status: 'planned',
          startDate: '2026-06-30',
          endDate: '2026-08-15',
          targetDate: '2026-08-12',
          allocations: [],
          children: [
            {
              id: SHIP_NODE_IDS.apiIntegration,
              code: 'T-104',
              kind: 'task',
              name: 'API integration',
              status: 'planned',
              startDate: '2026-07-07',
              endDate: '2026-08-01',
              targetDate: '2026-08-01',
              contractId: contractIds.a,
              roleRef: SHIP_ROLES.dev!,
              estimatedHours: 60,
              allocations: [],
              children: [],
            },
          ],
        },
        {
          id: SHIP_NODE_IDS.frontendImplementation,
          code: 'T-105',
          kind: 'task',
          name: 'Frontend implementation',
          status: 'planned',
          startDate: '2026-07-14',
          endDate: '2026-08-22',
          targetDate: '2026-08-22',
          contractId: contractIds.a,
          roleRef: SHIP_ROLES.dev!,
          estimatedHours: 140,
          allocations: [],
          children: [],
        },
      ],
    },
    {
      id: SHIP_NODE_IDS.testDeploy,
      code: 'D-3',
      kind: 'deliverable',
      name: 'Test & deploy',
      status: 'planned',
      startDate: '2026-08-18',
      endDate: '2026-09-12',
      targetDate: '2026-09-12',
      allocations: [],
      children: [
        {
          id: SHIP_NODE_IDS.qaTesting,
          code: 'T-106',
          kind: 'task',
          name: 'QA & testing',
          status: 'planned',
          startDate: '2026-08-18',
          endDate: '2026-09-05',
          targetDate: '2026-09-05',
          contractId: contractIds.b,
          /** Staffed to its budget, one line of it a role with nobody on it yet. */
          estimatedHours: 70,
          allocations: [
            {
              id: '00000000-0000-4000-8000-00000000b011',
              roleRef: SHIP_ROLES.qa!,
              personRef: SHIP_PEOPLE.lars!,
              hours: 50,
            },
            { id: '00000000-0000-4000-8000-00000000b012', roleRef: SHIP_ROLES.qa!, hours: 20 },
          ],
          children: [],
        },
        {
          id: SHIP_NODE_IDS.deployment,
          code: 'T-107',
          kind: 'task',
          name: 'Deployment & handover',
          status: 'planned',
          startDate: '2026-09-08',
          endDate: '2026-09-12',
          targetDate: '2026-09-12',
          contractId: contractIds.a,
          roleRef: SHIP_ROLES.devops!,
          estimatedHours: 30,
          allocations: [],
          children: [],
        },
      ],
    },
  ],
});

export type ProjectShipActualRow = {
  registrationId: string;
  employeeTlpId: string;
  planNodeId: string;
  registrationDate: string;
  hours: number;
};

/**
 * Time registrations matching the design seed (38 h on workshops, 52 h on
 * solution design). In production these rows arrive via the DataPlatform
 * connector; the POC read model links them by planNodeId in source_ref (OQ-1).
 */
export const createProjectShipActuals = (): ProjectShipActualRow[] => [
  {
    registrationId: 'ship-reg-001',
    employeeTlpId: 'u1',
    planNodeId: SHIP_NODE_IDS.workshops,
    registrationDate: '2026-06-05',
    hours: 20,
  },
  {
    registrationId: 'ship-reg-002',
    employeeTlpId: 'u1',
    planNodeId: SHIP_NODE_IDS.workshops,
    registrationDate: '2026-06-12',
    hours: 18,
  },
  {
    registrationId: 'ship-reg-003',
    employeeTlpId: 'u2',
    planNodeId: SHIP_NODE_IDS.solutionDesign,
    registrationDate: '2026-06-16',
    hours: 30,
  },
  {
    registrationId: 'ship-reg-004',
    employeeTlpId: 'u3',
    planNodeId: SHIP_NODE_IDS.solutionDesign,
    registrationDate: '2026-06-20',
    hours: 12,
  },
  {
    registrationId: 'ship-reg-005',
    employeeTlpId: 'u1',
    planNodeId: SHIP_NODE_IDS.solutionDesign,
    registrationDate: '2026-06-24',
    hours: 10,
  },
];

/**
 * The same document with every plan node given a fresh id.
 *
 * The fixture's node ids are fixed constants, which is what makes it a fixture — tests and
 * the seed assert against `SHIP_NODE_IDS`. But a *user-created* sample project must not
 * reuse them: `work_items.id` is the plan node id verbatim (ADR 0021), so two projects
 * carrying the same node ids collide on the primary key and the second one cannot be
 * projected. Anything that creates a real project from a fixture plan pipes it through
 * here; the seed and the tests deliberately do not.
 *
 * Only `id` changes. Node ids are not referenced from anywhere else in the document —
 * `contractId` and `defaultContractId` point at contracts — so no rewiring is needed.
 * Iterative by BR-3: plans may be tens of thousands of nodes deep-ish and wide.
 */
export const remapNodeIds = (
  document: ProjectDocument,
): { document: ProjectDocument; idMap: Map<string, string> } => {
  const idMap = new Map<string, string>();
  const roots: PlanNode[] = [];
  const stack: { source: PlanNode; siblings: PlanNode[] }[] = [];

  for (let i = document.plan.length - 1; i >= 0; i -= 1) {
    stack.push({ source: document.plan[i]!, siblings: roots });
  }

  while (stack.length > 0) {
    const { source, siblings } = stack.pop()!;
    const id = globalThis.crypto.randomUUID();
    idMap.set(source.id, id);

    const children: PlanNode[] = [];
    siblings.push({ ...source, id, children });
    for (let i = source.children.length - 1; i >= 0; i -= 1) {
      stack.push({ source: source.children[i]!, siblings: children });
    }
  }

  return { document: { ...document, plan: roots }, idMap };
};

/** `remapNodeIds` for callers that do not need the mapping. */
export const withFreshNodeIds = (document: ProjectDocument): ProjectDocument =>
  remapNodeIds(document).document;
